import { OrderStatusEnum } from 'washday-sdk';
import moment from 'moment-timezone';
import { IOrder } from '../interfaces/Order';
import { getOrdersListV2 } from '../services/order.service';
import { getOrderStoreId } from './storeReceptionValidation';

export const CONSOLIDATED_TICKET_NON_CANCELLED_STATUS_FILTER = `[${
  Object.values(OrderStatusEnum)
    .filter((status) => status !== OrderStatusEnum.CANCELLED)
    .join(',')
}]`;

export interface ConsolidatedOrdersTicketFilters {
  customerId: string;
  storeId?: string;
  storeIds?: string[] | string;
  fromDate: string;
  toDate: string;
  limit: number;
  fetchSection: string;
  status: string;
}

export interface ConsolidatedOrdersFetchProgress {
  loaded: number;
  total: number;
}

export interface FetchAllConsolidatedTicketOrdersOptions {
  signal?: AbortSignal;
  onProgress?: (progress: ConsolidatedOrdersFetchProgress) => void;
  maxPages?: number;
}

export interface ConsolidatedTicketStoreScope {
  _id: string;
  timezone: string;
}

export interface AllStoresConsolidatedOrdersTicketFilters {
  customerId: string;
  stores: ConsolidatedTicketStoreScope[];
  fromDate: string;
  toDate: string;
  limit: number;
  fetchSection: string;
  status: string;
}

function createAbortError(): Error {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('The operation was aborted', 'AbortError');
  }
  const error = new Error('The operation was aborted');
  error.name = 'AbortError';
  return error;
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

export function isConsolidatedTicketOrderEligible(order: IOrder): boolean {
  return order.status !== OrderStatusEnum.CANCELLED && !order.cancelledDateTime;
}

export async function fetchAllConsolidatedTicketOrders(
  filters: ConsolidatedOrdersTicketFilters,
  options: FetchAllConsolidatedTicketOrdersOptions = {}
): Promise<IOrder[]> {
  const maxPages = options.maxPages ?? 1000;
  const ordersById = new Map<string, IOrder>();
  let rawRowsLoaded = 0;

  for (let pageNum = 0; pageNum < maxPages; pageNum += 1) {
    throwIfAborted(options.signal);
    const response = await getOrdersListV2({
      ...filters,
      pageNum,
      sequenceSort: '-1',
      ...(options.signal ? { signal: options.signal } : {})
    });
    throwIfAborted(options.signal);

    const data = response?.data?.data;
    if (!data) {
      throw new Error('Unable to fetch consolidated ticket orders.');
    }

    const pageOrders = (data.orders || []) as IOrder[];
    const totalRows = Number(data.totalRows?.[0]?.totalRowsCount || 0);
    rawRowsLoaded += pageOrders.length;

    pageOrders.forEach((order) => {
      if (order._id && isConsolidatedTicketOrderEligible(order)) {
        ordersById.set(order._id, order);
      }
    });
    options.onProgress?.({ loaded: rawRowsLoaded, total: totalRows });

    if (pageOrders.length === 0 || (
      totalRows > 0
        ? rawRowsLoaded >= totalRows
        : pageOrders.length < filters.limit
    )) {
      return [...ordersById.values()];
    }
  }

  throw new Error(`Consolidated ticket page limit (${maxPages}) reached before all rows were loaded.`);
}

export async function fetchAllStoresConsolidatedTicketOrders(
  filters: AllStoresConsolidatedOrdersTicketFilters,
  options: FetchAllConsolidatedTicketOrdersOptions = {}
): Promise<IOrder[]> {
  const stores = [...filters.stores]
    .filter((store) => Boolean(store._id && store.timezone))
    .sort((left, right) => left._id.localeCompare(right._id));
  if (stores.length === 0) return [];

  const starts = stores.map((store) =>
    moment.tz(filters.fromDate, 'YYYY-MM-DD', store.timezone).startOf('day')
  );
  const ends = stores.map((store) =>
    moment.tz(filters.toDate, 'YYYY-MM-DD', store.timezone).endOf('day')
  );
  const fromDate = moment.min(starts).toISOString();
  const toDate = moment.max(ends).toISOString();
  const storeTimezones = new Map(stores.map((store) => [store._id, store.timezone]));

  const orders = await fetchAllConsolidatedTicketOrders({
    customerId: filters.customerId,
    storeIds: stores.map((store) => store._id),
    fromDate,
    toDate,
    limit: filters.limit,
    fetchSection: filters.fetchSection,
    status: filters.status
  }, options);

  return orders.filter((order) => {
    const timezone = storeTimezones.get(getOrderStoreId(order));
    if (!timezone || !order.createdDate) return false;
    const createdDate = moment(order.createdDate);
    if (!createdDate.isValid()) return false;
    const localDate = createdDate.tz(timezone).format('YYYY-MM-DD');
    return localDate >= filters.fromDate && localDate <= filters.toDate;
  });
}
